import { readLinkedDetail, linkedShareState, LINKED_SHARE_RATE_REMEDY, type LinkedDetail } from "@/lib/linked_splits";
import { defaultProfile } from "@/lib/profile_defaults";
import type { LocalExpense } from "@/lib/types";
/** Split reads and commands are local; only the background queue touches sync (W-1, INV-4). */
import { useState } from "react";
import { useLiveQuery } from "dexie-react-hooks";
import { db } from "@/lib/db";
import * as repo from "@/lib/repo";
import { nativeBridge } from "@/native/runtime";
import { computeShares, myShareHome, SplitRowError, MissingHomeRateError } from "@/domain/splits";
import { useFamilyData } from "./use_family_data";
import { useLapse } from "./use_lapse";
import { useUndoWindow } from "./use_undo_window";

export const SPLIT_LOCK_COPY = "This is your share of a split — edit the split instead";
export function splitErrorCopy(cause: unknown, fallback = "Couldn't save that change"): string {
  if (cause instanceof repo.SplitExpenseLockedError) return cause.expense.splitRef?.linkId ? "This is your share of a linked split — update from split instead" : SPLIT_LOCK_COPY;
  return cause instanceof Error ? `${fallback}: ${cause.message}` : `${fallback}. Please try again.`;
}

export function useSplitGroupList(includeArchived = false) {
  const result = useLiveQuery(async () => {
    try { return { groups: await repo.listSplitGroups({ includeArchived }), error: null }; }
    catch (cause) { return { groups: [], error: splitErrorCopy(cause) }; }
  }, [includeArchived]);
  return { groups: result?.groups ?? [], loaded: result !== undefined, error: result?.error ?? null };
}

export function useSplitLedgerShares(splitIds: readonly string[]) {
  const ids = [...new Set(splitIds)].sort();
  const key = JSON.stringify(ids);
  return useLiveQuery(async () => {
    const owner = repo.requireUser();
    const shares = await db.expenses.where("splitRef.splitExpenseId").anyOf(ids).filter(row => row.userId === owner && row.deletedAt === null).toArray();
    return new Map(shares.map(row => [row.splitRef!.splitExpenseId, row]));
  }, [key], new Map<string, LocalExpense>());
}

export function useSplitGroups(includeArchived = false) {
  const result = useLiveQuery(async () => {
    try {
      const groups = await repo.listSplitGroups({ includeArchived });
      const details = await Promise.all(groups.map(group => repo.getSplitGroupDetail(group.id)));
      return { groups, details, error: null };
    } catch (cause) { return { groups: [], details: [], error: splitErrorCopy(cause) }; }
  }, [includeArchived]);
  return { groups: result?.groups ?? [], details: result?.details ?? [], loaded: result !== undefined, error: result?.error ?? null };
}

export function useSplitGroup(groupId: string | undefined) {
  const result = useLiveQuery(async () => {
    try {
      if (!groupId) return { detail: null, baseLocked: false, error: null };
      const detail = await repo.getSplitGroupDetail(groupId);
      // Tombstoned history also locks the currency; live detail alone cannot answer this.
      const baseLocked = detail !== null && ((await db.splitExpenses.where("groupId").equals(groupId).count()) > 0
        || (await db.splitTransfers.where("groupId").equals(groupId).count()) > 0);
      return { detail, baseLocked, error: null };
    } catch (cause) { return { detail: null, baseLocked: false, error: splitErrorCopy(cause) }; }
  }, [groupId]);
  return { detail: result?.detail ?? null, baseLocked: result?.baseLocked ?? false, loaded: result !== undefined, error: result?.error ?? null };
}

export function useSplitMeName(): string {
  const data = useFamilyData();
  return data?.family?.members.find(member => member.member_id === data.family.my_member_id)?.display_name.trim() || "Me";
}

export function useSplitCommands() {
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const { captureBlocked, isCaptureBlocked } = useLapse();
  const reportError = (cause: unknown): void => setError(splitErrorCopy(cause));
  const undo = useUndoWindow<{ message: string; snapshot: repo.SplitUndoSnapshot }>({
    onUndo: value => repo.restoreSplitRows(value.snapshot), onError: reportError,
  });
  async function write<T>(action: () => Promise<T>, message: string, snapshot: (value: T) => repo.SplitUndoSnapshot, offerUndo = true): Promise<T> {
    if (isCaptureBlocked()) throw new Error("Your plan has ended. Renew to save changes.");
    setError(null); setNotice(null);
    const value = await action();
    if (offerUndo) undo.open({ message, snapshot: snapshot(value) });
    return value;
  }
  return {
    error, notice, captureBlocked, undo, reportError,
    create: (input: Parameters<typeof repo.createSplitGroup>[0], offerUndo = true) => write(() => repo.createSplitGroup(input), "Group created", group => ({ groups: [{ ...group, deletedAt: group.updatedAt }], splitExpenses: [], transfers: [], expenses: [] }), offerUndo),
    update: (id: string, patch: repo.SplitGroupPatch) => write(() => repo.updateSplitGroup(id, patch), "Group updated", value => value.undo),
    save: (input: repo.NewSplitExpense, options: Parameters<typeof repo.saveSplitExpense>[1]) => write(() => repo.saveSplitExpense(input, options), "Split saved", value => value.undo),
    deleteExpense: (id: string) => write(() => repo.deleteSplitExpense(id), "Split deleted", value => value),
    archive: (id: string) => write(() => repo.archiveSplitGroup(id), "Group archived", value => value),
    deleteGroup: (id: string) => write(() => repo.deleteSplitGroup(id), "Group deleted", value => value),
    recordTransfer: (input: repo.NewSplitTransfer) => write(() => repo.recordTransfer(input), "Transfer recorded", value => value.undo),
    updateTransfer: (id: string, patch: Parameters<typeof repo.updateTransfer>[1]) => write(() => repo.updateTransfer(id, patch), "Transfer updated", value => value.undo),
    deleteTransfer: (id: string) => write(() => repo.deleteTransfer(id), "Transfer deleted", value => value),
    fixShare: async (splitExpenseId: string, categoryId: string) => {
      const split = await db.splitExpenses.get(splitExpenseId);
      if (!split) throw new Error("Open the split group to repair this expense.");
      const group = await db.splitGroups.get(split.groupId);
      if (!group) throw new Error("Open Splits to repair this group.");
      const profile = await db.profile.get(repo.requireUser());
      const shares = await db.expenses.where("splitRef.splitExpenseId").equals(splitExpenseId).filter(row => row.userId === repo.requireUser() && row.deletedAt === null).toArray();
      const currency = profile?.currency ?? defaultProfile(repo.requireUser(), "").currency;
      const frozen = shares.find(row => row.splitRef?.homeCurrency && row.splitRef.homeCurrency !== currency);
      if (frozen) throw new Error(`This share was saved in ${frozen.splitRef!.homeCurrency}. Switch your profile currency back to ${frozen.splitRef!.homeCurrency} in Settings before fixing it.`);
      return write(() => repo.saveSplitExpense(split, { categoryId, homeRateNano: split.homeRateNano }), "Share fixed", value => value.undo);
    },
    copySummary: async (text: string) => { await navigator.clipboard.writeText(text); setNotice("Copied"); },
    shareSummary: async (title: string, text: string) => {
      setError(null); setNotice(null);
      try {
        const bridge = nativeBridge();
        if (bridge.share) await bridge.share({ title, text });
        else if (typeof navigator.share === "function") await navigator.share({ title, text });
        else { await navigator.clipboard.writeText(text); setNotice("Copied"); }
      } catch (cause) {
        if ((cause instanceof Error || cause instanceof DOMException) && cause.name === "AbortError") return;
        reportError(cause);
      }
    },
  };
}
export type SplitCommands = ReturnType<typeof useSplitCommands>;

export interface SplitShareStatus { name: string | null; mismatch: boolean; error: string | null }

/** Batch owned references; linked statuses read the separate cached group. */
export async function readSplitShareStatuses(rows: readonly LocalExpense[], homeCurrency: string): Promise<Map<string, SplitShareStatus>> {
  const owner = repo.requireUser();
  const shares = rows.filter(row => row.userId === owner && row.splitRef);
  const groupIds = [...new Set(shares.map(row => row.splitRef!.groupId))].sort();
  const splitIds = [...new Set(shares.map(row => row.splitRef!.splitExpenseId))].sort();
  const [groups, splits] = await Promise.all([db.splitGroups.bulkGet(groupIds), db.splitExpenses.bulkGet(splitIds)]);
  const groupMap = new Map(groups.filter(group => !!group).map(group => [group.id, group]));
  const splitMap = new Map(splits.filter(split => !!split).map(split => [split.id, split]));
  const statuses = new Map<string, SplitShareStatus>();
  const linkedDetails = new Map<string, LinkedDetail | null>();
  for (const row of shares) {
    const ref = row.splitRef!;
    if (ref.linkId) {
      try {
        if (!linkedDetails.has(ref.linkId)) linkedDetails.set(ref.linkId, await readLinkedDetail(ref.linkId));
        const detail = linkedDetails.get(ref.linkId);
        const source = detail?.expenses.find(expense => expense.id === ref.splitExpenseId);
        const state = detail && source ? linkedShareState(detail, source, row, homeCurrency) : null;
        statuses.set(ref.splitExpenseId, { name: detail?.group.name ?? null, mismatch: state === "update", error: state === "unreadable" ? LINKED_SHARE_RATE_REMEDY : state === "deleted" ? "Your share is no longer owed. Review Delete yours? in the shared group." : !detail ? "No longer shared. This is now an ordinary expense." : null });
      } catch (cause) {
        if (!(cause instanceof SplitRowError || cause instanceof MissingHomeRateError)) throw cause;
        statuses.set(ref.splitExpenseId, { name: "a shared split", mismatch: false, error: LINKED_SHARE_RATE_REMEDY });
      }
      continue;
    }
    const group = groupMap.get(ref.groupId), split = splitMap.get(ref.splitExpenseId);
    if (!group || !split || group.userId !== owner || split.userId !== owner || group.deletedAt !== null || split.deletedAt !== null || split.groupId !== group.id) {
      statuses.set(ref.splitExpenseId, { name: null, mismatch: false, error: "Open Splits to repair this expense." });
      continue;
    }
    try {
      if (ref.homeCurrency && ref.homeCurrency !== homeCurrency) {
        statuses.set(split.id, { name: group.name, mismatch: false, error: null }); continue;
      }
      const nativeShare = computeShares(split.amountMinor, split.mode, split.entries, split.currency).get(group.meMemberId) ?? 0;
      const expected = nativeShare === 0 ? 0 : myShareHome(split, group.meMemberId, homeCurrency, split.homeRateNano, group.baseCurrency, group.members.map(member => member.id));
      statuses.set(split.id, { name: group.name, mismatch: expected !== row.amountCents, error: null });
    } catch (cause) {
      if (!(cause instanceof SplitRowError || cause instanceof MissingHomeRateError)) throw cause;
      statuses.set(split.id, { name: group.name, mismatch: false, error: splitErrorCopy(cause, "Couldn't read your share") });
    }
  }
  return statuses;
}

export function useSplitShareStatuses(rows: readonly LocalExpense[], homeCurrency: string) {
  const key = JSON.stringify(rows.filter(row => row.splitRef).map(row => [row.id, row.userId, row.amountCents, row.splitRef]));
  return useLiveQuery(() => readSplitShareStatuses(rows, homeCurrency), [key, homeCurrency], new Map<string, SplitShareStatus>());
}
